import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { Badge, CardText } from 'reactstrap';

import { APIConfig } from 'src/config';
import Time from 'src/enosikit/components/Time/components/Time';
import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, UNTRADED_ENERGY_KEY } from 'src/util/constants';
import getUserDisplayName from 'src/util/i18n/decorators/getUserDisplayName';
import { getStringAndNumericFormattedDate, getTradeDirectionActionAndPreposition } from 'src/util/i18n/helpers';
import { buildTradeRuleMeterLabel, getUntradedContent } from 'src/util/i18n/trade';
import { getPriceRange } from 'src/util/tradeRule';
import { buildTradeRulePriceInformation } from 'src/util/i18n/tradeRule';

/**
 * Build the text content that needs to be shown inside the trade rule card.
 * @param {object} props
 * @param {object} props.tradeRule - trade rule object
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} props.direction
 * @param {string} props.type - trade rule type
 * @param {string} props.propertyTitle - property title
 * @returns {React.ReactNode | React.ReactElement | string} - trade rule card text content.
 */
function TradeRuleCardText(props) {
  if (Object.keys(props).length === 0) {
    return null;
  }

  const {
    tradeRule, direction, type, propertyTitle,
  } = props;
  const {
    seller, buyer, acceptedAt, acceptedBy, proposedAt,
    proposedBy, id: ruleId,
  } = tradeRule || {};
  const isBuy = direction === TRADE_DIRECTION_BUY;
  const trader = isBuy ? buyer : seller;
  const counterparty = isBuy ? seller : buyer;
  const priceRange = getPriceRange(tradeRule);
  const { maximum, minimum } = priceRange || {};
  const mode = APIConfig().MODE;
  const intl = useIntl();
  const tradeDirectionActionAndPreposition = getTradeDirectionActionAndPreposition(intl, direction);
  const tradeRuleMeterLabel = buildTradeRuleMeterLabel(
    intl,
    trader,
    counterparty,
    direction,
    true,
  );

  const meterLabelText = intl.formatMessage({
    id: 'trade_summary.trade_rule_card_text.meter_label.details.text',
    defaultMessage: '{tradeDirectionActionAndPreposition} {tradeRuleMeterLabel}',
  }, { tradeDirectionActionAndPreposition, tradeRuleMeterLabel });

  const noMeterLabelText = intl.formatMessage(
    {
      id: 'trade_summary.trade_rule_card_text.proposal.details.text',
      defaultMessage: 'Proposed by {actor} on {timestamp}',
    },
    {
      actor: <strong>{getUserDisplayName(intl, proposedBy)}</strong>,
      timestamp: <Time
        child={getStringAndNumericFormattedDate(intl, proposedAt)}
        unixTimestamp={proposedAt}
      />,
    },
  );

  const meterLabel = tradeRuleMeterLabel ? meterLabelText : noMeterLabelText;

  const acceptedAtText = intl.formatMessage(
    {
      id: 'trade_summary.trade_rule_card_text.trade_acceptance.details.text',
      defaultMessage: 'Accepted by {actor} on {timestamp}',
    },
    {
      actor: <strong>{getUserDisplayName(intl, acceptedBy)}</strong>,
      timestamp: <Time
        child={getStringAndNumericFormattedDate(intl, acceptedAt)}
        unixTimestamp={acceptedAt}
      />,
    },

  );

  if (type === UNTRADED_ENERGY_KEY) {
    return getUntradedContent(intl, propertyTitle);
  }
  return (
    <CardText>
      {meterLabel}
      <br />
      {acceptedAt && acceptedAtText}
      <br />
      {buildTradeRulePriceInformation({
        direction, intl, maximum, minimum, mode,
      })}
      <Badge color="mid">{ruleId}</Badge>
    </CardText>
  );
}

TradeRuleCardText.propTypes = {
  tradeRule: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  direction: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  propertyTitle: PropTypes.string.isRequired,
};

export default TradeRuleCardText;
